<div *ngIf="isVisible" class="relative z-50" aria-labelledby="proceed-leads-title" role="dialog" aria-modal="true">
  <div class="fixed inset-0 bg-secondary-900 bg-opacity-50 backdrop-blur-sm transition-opacity" (click)="close()"></div>

  <div class="fixed inset-0 z-10 w-screen overflow-y-auto" (click)="close()">
    <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
      <div class="px-6 pt-6 pb-6 relative transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl"
           (click)="$event.stopPropagation()">

        <!-- Close -->
        <div class="absolute right-0 top-0 pr-4 pt-4">
          <button type="button" (click)="close()" [disabled]="isContinuing"
                  class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none disabled:opacity-50">
            <span class="sr-only">Close</span>
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <h3 id="proceed-leads-title" class="text-lg font-semibold text-gray-900 mb-1">Proceed to Leads</h3>
        <p class="text-sm text-gray-500 mb-4">
          <span class="font-semibold text-gray-800">{{ totalRecords | number:'1.0-0' }}</span>
          compan{{ totalRecords === 1 ? 'y matches' : 'ies match' }} your search. Choose who to find at these companies.
        </p>

        <!-- Mapping preview -->
        <div class="rounded-lg bg-gray-50 border border-gray-100 p-4 mb-5">
          <!-- Loading skeleton -->
          <div *ngIf="isLoadingPreview" class="space-y-2 animate-pulse" aria-label="Loading search mapping">
            <div class="h-3 bg-gray-200 rounded w-1/3"></div>
            <div class="flex gap-2">
              <div class="h-5 bg-gray-200 rounded-full w-20"></div>
              <div class="h-5 bg-gray-200 rounded-full w-28"></div>
              <div class="h-5 bg-gray-200 rounded-full w-16"></div>
            </div>
            <div class="h-3 bg-gray-200 rounded w-1/4 mt-3"></div>
            <div class="h-5 bg-gray-200 rounded-full w-32"></div>
          </div>

          <!-- Error (non-blocking) -->
          <div *ngIf="!isLoadingPreview && previewError" class="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <span>Couldn't load how your search maps to lead targeting. You can still continue.</span>
            <button type="button" class="text-primary font-medium hover:underline" (click)="loadPreview()">Retry</button>
          </div>

          <ng-container *ngIf="!isLoadingPreview && preview">
            <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Used for matching</div>
            <div *ngIf="!mappedGroups.length" class="text-sm text-gray-400 mb-2">
              None of your filters map to lead targeting — every company in the search is used.
            </div>
            <div *ngFor="let group of mappedGroups" class="flex flex-wrap items-center gap-1.5 mb-2">
              <span class="text-xs text-gray-500 mr-1">{{ group.label }}:</span>
              <span *ngFor="let value of group.values"
                    class="inline-flex items-center rounded-full bg-violet-50 border border-violet-200 text-violet-700 text-xs font-medium px-2 py-0.5">
                {{ value }}
              </span>
            </div>

            <ng-container *ngIf="listOnly.length">
              <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-3 mb-2">List-only filters</div>
              <div class="flex flex-wrap gap-1.5">
                <span *ngFor="let item of listOnly"
                      class="inline-flex items-center rounded-full bg-white border border-gray-200 text-gray-600 text-xs px-2 py-0.5">
                  {{ item.label }}: {{ item.value }}
                </span>
              </div>
              <p class="text-xs text-gray-400 mt-1">These narrow down this list but can't be saved into an ICP.</p>
            </ng-container>

            <ul *ngIf="warnings.length" class="mt-3 space-y-1">
              <li *ngFor="let warning of warnings" class="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1">
                {{ warning }}
              </li>
            </ul>
          </ng-container>
        </div>

        <!-- Job titles -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Job titles</label>
          <div class="flex flex-wrap gap-1.5 mb-2">
            <button *ngFor="let preset of titlePresets" type="button"
                    class="rounded-full border text-xs px-2.5 py-1 transition-colors"
                    [ngClass]="hasValue(jobTitles, preset) ? 'bg-violet-600 border-violet-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'"
                    [disabled]="isContinuing"
                    (click)="togglePreset('title', preset)">
              {{ preset }}
            </button>
          </div>
          <div class="flex gap-2">
            <input type="text" [(ngModel)]="titleInput" [disabled]="isContinuing"
                   (keydown.enter)="addFromInput('title')"
                   placeholder="Add another title (Enter or comma-separated)"
                   class="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-violet-500 focus:border-violet-500">
            <button type="button" class="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    [disabled]="!titleInput.trim() || isContinuing" (click)="addFromInput('title')">Add</button>
          </div>
          <div *ngIf="jobTitles.length" class="flex flex-wrap gap-1.5 mt-2">
            <span *ngFor="let title of jobTitles"
                  class="inline-flex items-center gap-1 rounded-full bg-violet-50 border border-violet-200 text-violet-700 text-xs font-medium pl-2.5 pr-1 py-0.5">
              {{ title }}
              <button type="button" class="rounded-full p-0.5 hover:bg-violet-100 text-violet-400 hover:text-violet-700"
                      [attr.aria-label]="'Remove ' + title" [disabled]="isContinuing" (click)="remove('title', title)">
                <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </span>
          </div>
        </div>

        <!-- Departments -->
        <div class="mb-2">
          <label class="block text-sm font-medium text-gray-700 mb-1">Departments</label>
          <div class="flex flex-wrap gap-1.5 mb-2">
            <button *ngFor="let preset of departmentPresets" type="button"
                    class="rounded-full border text-xs px-2.5 py-1 transition-colors"
                    [ngClass]="hasValue(departments, preset) ? 'bg-violet-600 border-violet-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'"
                    [disabled]="isContinuing"
                    (click)="togglePreset('department', preset)">
              {{ preset }}
            </button>
          </div>
          <div class="flex gap-2">
            <input type="text" [(ngModel)]="departmentInput" [disabled]="isContinuing"
                   (keydown.enter)="addFromInput('department')"
                   placeholder="Add another department"
                   class="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-violet-500 focus:border-violet-500">
            <button type="button" class="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    [disabled]="!departmentInput.trim() || isContinuing" (click)="addFromInput('department')">Add</button>
          </div>
          <div *ngIf="departments.length" class="flex flex-wrap gap-1.5 mt-2">
            <span *ngFor="let department of departments"
                  class="inline-flex items-center gap-1 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-xs font-medium pl-2.5 pr-1 py-0.5">
              {{ department }}
              <button type="button" class="rounded-full p-0.5 hover:bg-sky-100 text-sky-400 hover:text-sky-700"
                      [attr.aria-label]="'Remove ' + department" [disabled]="isContinuing" (click)="remove('department', department)">
                <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </span>
          </div>
        </div>

        <p *ngIf="showValidation && !hasPeopleTargets" class="text-sm text-red-600 mt-2">
          Pick at least one job title or department.
        </p>
        <p class="text-xs text-gray-400 mt-2">
          Leads stay in an unsaved prospecting list for 24 h after they're generated — save it as an ICP to keep it.
        </p>

        <div class="flex gap-3 mt-6">
          <button type="button" (click)="close()" [disabled]="isContinuing"
                  class="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none disabled:opacity-50">
            Cancel
          </button>
          <button type="button" (click)="proceed()" [disabled]="isContinuing"
                  class="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:opacity-90 focus:outline-none disabled:opacity-60">
            <svg *ngIf="isContinuing" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            {{ isContinuing ? 'Opening leads…' : 'Continue to Leads' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
